import { Money } from "@despejo/domain";
import { type PartialPaymentsReconciliation, pctChange } from "./partialPayments.js";
import type { RentaResult, RentaTaxpayerType } from "./renta.js";

/**
 * Year-end close for the annual Renta return (plan §17.2, DES-77) and the
 * comparison of the running estimate with the return filed the year before.
 * Pure: the caller passes the Renta estimate, the pagos parciales
 * reconciliation, the retentions and the manual confirmations; every item
 * carries the ledger / obligation references behind it for the drill-down.
 *
 * Items are AUTO (derived from data) or MANUAL (a person confirms them); an
 * AUTO item that finds nothing wrong is OK. A MANUAL confirmation ("CONFIRMED"
 * or "NOT_APPLICABLE") also settles an AUTO item that only asks for a review
 * (e.g. months without income in a seasonal business), never a BLOCKING one.
 */

export const RENTA_CLOSE_VERSION = "renta-close@1.0.0";

export type CloseItemCode =
  | "EXCHANGE_RATES"
  | "UNCLASSIFIED"
  | "UNACCEPTED_RECEIPTS"
  | "INCOME_COMPLETE"
  | "NON_DEDUCTIBLE"
  | "PARTIAL_PAYMENTS"
  | "RETENTIONS"
  | "PRIOR_RETURN"
  | "DEPRECIATION"
  | "RELATED_PARTIES"
  | "FOREIGN_OPERATIONS"
  | "LOSSES"
  | "FAMILY_CREDITS"
  | "BALANCES";

export type CloseStatus = "OK" | "ATTENTION" | "BLOCKING" | "REVIEW" | "CONFIRMED" | "NOT_APPLICABLE";

export interface CloseReview {
  code: string;
  status: "CONFIRMED" | "NOT_APPLICABLE";
  note?: string | null;
}

export interface CloseItem {
  code: CloseItemCode;
  label: string;
  status: CloseStatus;
  source: "AUTO" | "MANUAL";
  /** Whether a person may settle it with a confirmation. */
  reviewable: boolean;
  detail: string;
  /** Ledger / obligation / row references behind the item (drill-down). */
  refs: string[];
  amount: string | null;
  basis: string | null;
  review: CloseReview | null;
}

export interface CloseRetention {
  id: string;
  amount: string;
  withholderName: string;
  hasEvidence: boolean;
}

/** Figures of a filed 101/102 (or D-101) as captured from the return (renta_prior_returns). */
export interface PriorReturnFigures {
  ref: string;
  fiscalYear: number;
  form: string;
  kind: "ORIGINAL" | "RECTIFICATIVA";
  capturedAt: string;
  grossIncome: string;
  deductions: string;
  netIncome: string;
  grossTax: string;
  familyCredits: string;
  partialPayments: string;
  retentions: string;
  balanceDue: string;
  creditBalance: string;
}

export interface RentaCloseInput {
  fiscalYear: number;
  taxpayerType: RentaTaxpayerType;
  renta: RentaResult;
  /** Null when pagos parciales do not apply or could not be reconciled. */
  partialPayments: PartialPaymentsReconciliation | null;
  partialPaymentsApply: boolean;
  retentions: readonly CloseRetention[];
  /** The filed return of fiscalYear - 1, when captured. */
  priorReturn: PriorReturnFigures | null;
  /** Months (1-12) of the fiscal year with at least one income entry in the ledger. */
  incomeMonths: readonly number[];
  /** Months of the year the ledger should cover by now (12 for a closed year). */
  monthsExpected: number;
  reviews: readonly CloseReview[];
}

export interface RentaCloseChecklist {
  engine: string;
  fiscalYear: number;
  items: CloseItem[];
  counts: Record<"blocking" | "attention" | "review" | "done", number>;
  /** Nothing blocking, nothing to fix and every review done. */
  ready: boolean;
}

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "set", "oct", "nov", "dic"];
const r2 = (m: Money) => m.round(2).toFixed(2);
const DONE = new Set<CloseStatus>(["OK", "CONFIRMED", "NOT_APPLICABLE"]);

export function rentaCloseChecklist(input: RentaCloseInput): RentaCloseChecklist {
  const r = input.renta;
  const isPF = input.taxpayerType === "PERSONA_FISICA";
  const reviewOf = (code: CloseItemCode) => input.reviews.find((x) => x.code === code) ?? null;
  const items: CloseItem[] = [];
  const refsOf = (code: string) => r.exceptions.filter((e) => e.code === code).map((e) => e.ref);

  /** An item from data; `issue` = what is wrong (null = OK). A review settles a non-blocking issue. */
  const auto = (
    code: CloseItemCode,
    label: string,
    issue: { status: "ATTENTION" | "BLOCKING" | "REVIEW"; detail: string; refs?: string[]; amount?: string | null } | null,
    okDetail: string,
    basis: string | null,
    reviewable = true,
  ) => {
    const review = reviewable ? reviewOf(code) : null;
    const settled = review && issue && issue.status !== "BLOCKING";
    items.push({
      code,
      label,
      status: !issue ? "OK" : settled ? review.status : issue.status,
      source: "AUTO",
      reviewable: reviewable && (!issue || issue.status !== "BLOCKING"),
      detail: issue ? issue.detail : okDetail,
      refs: issue?.refs ?? [],
      amount: issue?.amount ?? null,
      basis,
      review: settled ? review : null,
    });
  };
  const manual = (code: CloseItemCode, label: string, detail: string, basis: string | null = null) => {
    const review = reviewOf(code);
    items.push({ code, label, status: review ? review.status : "REVIEW", source: "MANUAL", reviewable: true, detail, refs: [], amount: null, basis, review });
  };

  // ------------------------------------------------------------ ledger quality
  const noRate = refsOf("MISSING_EXCHANGE_RATE");
  auto(
    "EXCHANGE_RATES",
    "Tipos de cambio",
    noRate.length ? { status: "BLOCKING", detail: `${noRate.length} movimiento(s) en moneda extranjera sin tipo de cambio quedaron fuera del estimado: registre el tipo de cambio.`, refs: noRate } : null,
    "Todos los movimientos en moneda extranjera tienen tipo de cambio.",
    "Ley 7092 art. 5: conversión al tipo de cambio de referencia para la venta del BCCR",
    false,
  );

  const unclassified = refsOf("UNCLASSIFIED_EXPENSE");
  auto(
    "UNCLASSIFIED",
    "Gastos sin clasificar",
    unclassified.length ? { status: "ATTENTION", detail: `${unclassified.length} línea(s) de gasto sin clasificar para renta: no se deducen hasta clasificarlas.`, refs: unclassified, amount: r.unclassified } : null,
    "Todas las líneas de gasto están clasificadas.",
    null,
    false,
  );

  const unaccepted = refsOf("UNACCEPTED_RECEIPT");
  const pendingAcceptance = refsOf("ACCEPTANCE_PENDING");
  auto(
    "UNACCEPTED_RECEIPTS",
    "Comprobantes recibidos sin aceptar",
    unaccepted.length || pendingAcceptance.length
      ? {
          status: "ATTENTION",
          detail: [
            unaccepted.length ? `${unaccepted.length} comprobante(s) rechazados o sin aceptación en plazo se tratan como no deducibles.` : "",
            pendingAcceptance.length ? `${pendingAcceptance.length} comprobante(s) todavía pueden aceptarse: hágalo antes del plazo.` : "",
          ]
            .filter(Boolean)
            .join(" "),
          refs: [...unaccepted, ...pendingAcceptance],
          amount: unaccepted.length ? r.notAccepted : null,
        }
      : null,
    "Todos los comprobantes recibidos que lo requieren están aceptados.",
    "Reglamento 44739-H art. 20; RES-0027-2024 art. 10 (docs/tax/RECEIPT_ACCEPTANCE.md §2.2) [verificar]",
    false,
  );

  const missingMonths: number[] = [];
  for (let m = 1; m <= Math.min(12, Math.max(0, input.monthsExpected)); m++) if (!input.incomeMonths.includes(m)) missingMonths.push(m);
  auto(
    "INCOME_COMPLETE",
    "Ingresos completos",
    missingMonths.length
      ? { status: "ATTENTION", detail: `Meses sin ingresos en el libro: ${missingMonths.map((m) => MONTHS[m - 1]).join(", ")}. Registre las facturas emitidas y los ingresos sin comprobante, o confirme que no hubo.` }
      : { status: "REVIEW", detail: "Confirme que todas las facturas emitidas y los demás ingresos del año están en el libro." },
    "",
    "Ley 7092 art. 5 (renta bruta del periodo)",
  );

  auto(
    "NON_DEDUCTIBLE",
    "Gastos no deducibles",
    Money.of(r.notDeductible).isPositive() ? { status: "REVIEW", detail: "Revise que los gastos personales y no deducibles estén bien marcados.", refs: r.trace.filter((t) => t.bucket === "not_deductible").map((t) => t.ref), amount: r.notDeductible } : null,
    "No hay gastos marcados como no deducibles.",
    "Ley 7092 art. 8 (gastos deducibles)",
  );

  // ------------------------------------------------------------- credits
  if (!input.partialPaymentsApply) {
    items.push({ code: "PARTIAL_PAYMENTS", label: "Pagos parciales", status: "NOT_APPLICABLE", source: "AUTO", reviewable: false, detail: "No aplican pagos parciales a este contribuyente.", refs: [], amount: null, basis: "Ley 7092 art. 22", review: null });
  } else {
    const pp = input.partialPayments;
    const problems: string[] = [];
    const refs: string[] = [];
    let status: "ATTENTION" | "REVIEW" = "REVIEW";
    if (!pp) {
      problems.push("No se pudo conciliar los pagos parciales del año (falta la base o la regla).");
    } else {
      for (const row of pp.rows) {
        const ref = row.obligationId ?? `pp:${row.key}`;
        if (row.status === "OVERDUE") {
          problems.push(`${row.key} vencido con ${row.pending} pendiente.`);
          refs.push(ref);
          status = "ATTENTION";
        }
        for (const f of row.flags) {
          if (f.code === "OFFICIAL_DIFFERS" || f.code === "REQUEST_LATE" || f.code === "OVERPAID") {
            problems.push(`${row.key}: ${f.message}.`);
            refs.push(ref);
            status = "ATTENTION";
          }
        }
      }
      const noOfficial = pp.rows.filter((row) => row.official === null && row.status !== "NOT_DUE");
      if (noOfficial.length) {
        problems.push(`Falta el monto de la Cuenta Integral Tributaria de ${noOfficial.map((x) => x.key).join(", ")}.`);
        refs.push(...noOfficial.map((x) => x.obligationId ?? `pp:${x.key}`));
      }
      if (pp.credit.status === "MISMATCH") {
        problems.push(`La declaración acredita ${pp.credit.credited} de pagos parciales y se pagaron ${pp.credit.paid}.`);
        if (pp.credit.ref) refs.push(pp.credit.ref);
        status = "ATTENTION";
      }
    }
    auto(
      "PARTIAL_PAYMENTS",
      "Pagos parciales pagados vs esperados",
      problems.length ? { status, detail: problems.join(" "), refs: [...new Set(refs)], amount: pp ? pp.totals.pending : null } : null,
      pp ? `Pagado ${pp.totals.paid}; ${pp.credit.status === "MATCH" ? "coincide con lo acreditado en la declaración" : "cuotas al día"}.` : "",
      "Ley 7092 art. 22; Reglamento 43198-H art. 26 a)",
      status !== "ATTENTION",
    );
  }

  const noEvidence = input.retentions.filter((x) => !x.hasEvidence);
  auto(
    "RETENTIONS",
    "Retenciones y certificados",
    input.retentions.length === 0
      ? { status: "REVIEW", detail: "No hay retenciones registradas. Confirme que no le retuvieron renta en el año (p. ej. el 2% en contratos con el sector público)." }
      : noEvidence.length
        ? { status: "ATTENTION", detail: `${noEvidence.length} retención(es) sin certificado o comprobante adjunto: ${noEvidence.map((x) => x.withholderName).join(", ")}.`, refs: noEvidence.map((x) => x.id), amount: r2(Money.sum(noEvidence.map((x) => Money.of(x.amount)))) }
        : null,
    `${input.retentions.length} retención(es), todas con comprobante.`,
    "Reglamento 43198-H art. 26 b)-d)",
    input.retentions.length === 0,
  );

  auto(
    "PRIOR_RETURN",
    `Declaración ${input.fiscalYear - 1}`,
    input.priorReturn ? null : { status: "REVIEW", detail: `Registre las cifras de la declaración ${input.fiscalYear - 1} presentada (con el PDF): son la base de los pagos parciales y de la comparación. Si no hubo declaración, márquelo como no aplica.` },
    input.priorReturn ? `Formulario ${input.priorReturn.form} registrado (${input.priorReturn.kind === "RECTIFICATIVA" ? "rectificativa" : "original"}).` : "",
    "Ley 7092 art. 22 a)",
  );

  // ------------------------------------------------------ manual reviews (§17.2)
  manual("DEPRECIATION", "Depreciación y compras de activos", "Confirme que las compras de activos no se dedujeron como gasto del año y que la depreciación está calculada.");
  manual("RELATED_PARTIES", "Operaciones con partes relacionadas", "Confirme si hubo operaciones con socios, familiares o empresas vinculadas y que están documentadas.");
  manual("FOREIGN_OPERATIONS", "Operaciones con el exterior", "Confirme el tratamiento de ingresos y gastos con el exterior (fuente costarricense: dónde se presta el servicio).", "Ley 7092 art. 1");
  manual("LOSSES", "Pérdidas de periodos anteriores", "Confirme si hay pérdidas de años anteriores que se arrastran, o marque que no aplica.");
  if (isPF) manual("FAMILY_CREDITS", "Créditos por hijos y cónyuge", "Confirme los dependientes declarados y tenga a mano la certificación que los respalda.");
  else manual("BALANCES", "Saldos y balance", "Confirme los saldos iniciales y finales y que el balance cuadra con el estado de resultados.");

  const counts = { blocking: 0, attention: 0, review: 0, done: 0 };
  for (const i of items) {
    if (i.status === "BLOCKING") counts.blocking++;
    else if (i.status === "ATTENTION") counts.attention++;
    else if (i.status === "REVIEW") counts.review++;
    else counts.done++;
  }
  return { engine: RENTA_CLOSE_VERSION, fiscalYear: input.fiscalYear, items, counts, ready: items.every((i) => DONE.has(i.status)) };
}

// -------------------------------------------------------------- comparison

export interface ComparisonRow {
  key: "grossIncome" | "deductions" | "netIncome" | "grossTax" | "familyCredits" | "partialPayments" | "retentions" | "balance";
  label: string;
  prior: string;
  current: string;
  delta: string;
  /** Percent change with one decimal, null when the prior figure is zero. */
  pct: string | null;
}

/**
 * The running estimate of year Y (projected to twelve months) next to the
 * return filed for Y-1. `balance` is signed: positive = to pay, negative =
 * saldo a favor.
 */
export function compareWithPriorReturn(current: RentaResult, prior: PriorReturnFigures): { rows: ComparisonRow[]; notes: string[] } {
  const m = (x: string) => Money.of(x);
  const annualDeductions = m(current.annualGrossIncome).sub(m(current.annualNetIncome));
  const pairs: [ComparisonRow["key"], string, Money, Money][] = [
    ["grossIncome", "Ingresos brutos", m(current.annualGrossIncome), m(prior.grossIncome)],
    ["deductions", "Gastos deducibles", annualDeductions, m(prior.deductions)],
    ["netIncome", "Renta neta", m(current.annualNetIncome), m(prior.netIncome)],
    ["grossTax", "Impuesto según tarifa", m(current.grossTax), m(prior.grossTax)],
    ["familyCredits", "Créditos por hijos y cónyuge", m(current.familyCredits), m(prior.familyCredits)],
    ["partialPayments", "Pagos parciales", m(current.partialPayments), m(prior.partialPayments)],
    ["retentions", "Retenciones", m(current.retentions), m(prior.retentions)],
    ["balance", "Saldo (por pagar − a favor)", m(current.balanceDue).sub(m(current.creditBalance)), m(prior.balanceDue).sub(m(prior.creditBalance))],
  ];
  const rows = pairs
    .filter(([key, , cur, pri]) => current.taxpayerType === "PERSONA_FISICA" || key !== "familyCredits" || !cur.isZero() || !pri.isZero())
    .map(([key, label, cur, pri]) => ({ key, label, prior: r2(pri), current: r2(cur), delta: r2(cur.sub(pri)), pct: pctChange(r2(cur), r2(pri)) }));
  const notes: string[] = [];
  if (current.projectionFactor !== "1") notes.push(`Las cifras de ${current.fiscalYear} son una proyección a 12 meses (factor ${current.projectionFactor}).`);
  if (current.provisional) notes.push(`El estimado de ${current.fiscalYear} es provisional.`);
  if (!current.computable) notes.push(`No hay impuesto estimado para ${current.fiscalYear}: faltan parámetros.`);
  if (prior.kind === "RECTIFICATIVA") notes.push(`Las cifras de ${prior.fiscalYear} son de una declaración rectificativa.`);
  return { rows, notes };
}
